import React, { useCallback, useEffect, useState } from 'react';
import { PageContainer, PageHeader } from '@/components/layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { adminEnterpriseContractStatusApi, EnterpriseContractStatusItem } from '@/api/planApi';
import { LoadingBlock } from '@/components/ui/spinner';

// ── 상수 ─────────────────────────────────────────────────────
// 유료회원 결제 현황이 ENTERPRISE 플랜을 제외하는 것과 별개로, 계약(UserEnterpriseContract/
// TeamEnterpriseContract) 단위 데이터를 전용으로 모니터링한다.

const CONTRACT_STATUS_LABELS: Record<string, string> = {
  PENDING: '입금대기',
  ACTIVE: '정상',
  EXPIRED: '만료',
  CANCELLED: '취소',
  SUSPENDED: '정지',
};

const CONTRACT_STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  ACTIVE: 'bg-green-100 text-green-700 border-green-200',
  EXPIRED: 'bg-gray-100 text-gray-500 border-gray-200',
  CANCELLED: 'bg-gray-100 text-gray-500 border-gray-200',
  SUSPENDED: 'bg-red-100 text-red-700 border-red-200',
};

const WORKSPACE_STATUS_LABELS: Record<string, string> = {
  ACTIVE: '정상',
  PAST_DUE: '미납(유예중)',
  INACTIVE: '비활성',
};

const WORKSPACE_STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-700 border-green-200',
  PAST_DUE: 'bg-red-100 text-red-700 border-red-200',
  INACTIVE: 'bg-gray-100 text-gray-500 border-gray-200',
};

type FilterTab = 'MISMATCH' | 'ALL';

const FILTER_TABS: { key: FilterTab; label: string }[] = [
  { key: 'MISMATCH', label: '이상 상태만' },
  { key: 'ALL', label: '전체' },
];

// ── 컴포넌트 ─────────────────────────────────────────────────

export default function AdminEnterpriseContractStatus() {
  const [items, setItems] = useState<EnterpriseContractStatusItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<FilterTab>('MISMATCH');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminEnterpriseContractStatusApi.getList();
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError('엔터프라이즈 계약 현황을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredItems = items.filter((item) => (activeTab === 'MISMATCH' ? item.statusMismatch : true));
  const mismatchCount = items.filter((item) => item.statusMismatch).length;

  const formatDate = (iso: string) =>
    iso ? new Date(iso).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '-';

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        title="엔터프라이즈 계약 현황"
        description="개인/팀 엔터프라이즈 계약 상태와 연결된 Workspace 상태의 정합성을 모니터링합니다. 계약은 SUSPENDED인데 Workspace는 ACTIVE(또는 그 반대)이거나 연결된 Workspace가 없으면 이상 상태입니다."
        action={
          <div className="flex items-center gap-2">
            {mismatchCount > 0 && (
              <Badge className="bg-red-100 text-red-700 border border-red-300 text-sm px-3 py-1">
                이상 상태 {mismatchCount}건
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              새로고침
            </Button>
          </div>
        }
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-4">
          <div className="flex gap-1">
            {FILTER_TABS.map((tab) => (
              <Button
                key={tab.key}
                variant={activeTab === tab.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab(tab.key)}
                className="text-xs"
              >
                {tab.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <LoadingBlock size="lg" py={16} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>계약자</TableHead>
                    <TableHead>구분</TableHead>
                    <TableHead>계약명</TableHead>
                    <TableHead>월 이용료</TableHead>
                    <TableHead className="text-center">계약 상태</TableHead>
                    <TableHead className="text-center">Workspace 상태</TableHead>
                    <TableHead>계약 기간</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-gray-400 py-16 text-sm">
                        해당하는 계약이 없습니다.
                      </TableCell>
                    </TableRow>
                  ) : filteredItems.map((item) => (
                    <TableRow
                      key={`${item.contractType}-${item.contractId}`}
                      className={item.statusMismatch ? 'bg-red-50/60' : undefined}
                    >
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {item.statusMismatch && <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                          <div>
                            <p className="text-sm font-medium">{item.ownerName ?? item.ownerUserId ?? '-'}</p>
                            <p className="text-xs text-gray-500">{item.ownerEmail ?? item.ownerUserId ?? '-'}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">
                        {item.contractType === 'USER' ? '개인' : (item.teamName ?? '팀')}
                      </TableCell>
                      <TableCell className="text-sm">{item.contractName}</TableCell>
                      <TableCell className="text-sm">{item.monthlyPrice.toLocaleString()}원</TableCell>
                      <TableCell className="text-center">
                        <Badge className={`text-xs border ${CONTRACT_STATUS_COLORS[item.contractStatus] ?? 'bg-gray-100 text-gray-600'}`}>
                          {CONTRACT_STATUS_LABELS[item.contractStatus] ?? item.contractStatus}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {item.workspaceStatus ? (
                          <Badge className={`text-xs border ${WORKSPACE_STATUS_COLORS[item.workspaceStatus] ?? 'bg-gray-100 text-gray-600'}`}>
                            {WORKSPACE_STATUS_LABELS[item.workspaceStatus] ?? item.workspaceStatus}
                          </Badge>
                        ) : (
                          <Badge className="text-xs border bg-red-100 text-red-700 border-red-200">없음</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">
                        {formatDate(item.startDate)} ~ {formatDate(item.endDate)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
}
